import { Component, Inject, OnInit, effect, signal, untracked } from '@angular/core';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { AlertsApi, AlertSettings, UpdateAlertsReceivedRequest, MessageSettings, User } from './hickory-api/index';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { IncidentModelHelper } from './shared/incident';
import { ConfirmComponent } from './shared/modals/confirm/confirm.component';
import { AuthService } from './shared/authentication/auth.service';
import { SnackbarService } from './shared/snackbar.service';
import { AccessControl } from './shared/authentication/access-control';

@Component({
  styleUrls: ['./home.component.scss'],
  selector: 'app-home',
  templateUrl: './home.component.html',
  standalone: false
})
export class HomeComponent implements OnInit {

  fetchRecvInProgress = false;
  fetchSentInProgress = false;
  alertsSent: AlertSettings[];
  alertsReceived: AlertSettings[];
  unreadOnly: boolean = null;
  readonly showEnterpriseDashboard = this.authService.hasPermissionSignal(AccessControl.FeatureEnterpriseDashboard);
  private readonly alertsPending = signal(false);

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private router: Router,
    private alertsApi: AlertsApi,
    private authService: AuthService,
    private snack: SnackbarService,
    public dialog: MatDialog
  ) {
    effect(() => {
      if (this.alertsPending() && !this.showEnterpriseDashboard()) {
        this.alertsPending.set(false);
        untracked(() => this.loadAlerts());
      }
    });
  }

  ngOnInit() {
    this.checkUser(this.authService.user.snapshot);
  }

  checkUser(user: User) {
    if (!user) {
      // if we don't have a user value yet, fetch one and then try again
      this.authService.getCurrentUser().then(u => this.checkUser(u)).catch(_ => { /* not authenticated */ });
      return;
    }

    if (this.authService.isAdminUser(user)) {
      this.alertsPending.set(true);
    } else {
      this.router.navigateByUrl('/alerts/received?type=map');
    }
  }

  loadAlerts(receivedOnly: boolean = false) {
    if (!receivedOnly)  {
      this.fetchSentInProgress = true;
      this.alertsApi.getAlertsSentV3(undefined, undefined, undefined, undefined, undefined, false, undefined, undefined, 1, 10).subscribe(r => {
        this.alertsSent = r.items;
        this.fetchSentInProgress = false;
      });
    }
    this.fetchRecvInProgress = true;
    this.alertsApi.getAlertsReceived(undefined, undefined, undefined, undefined, false, undefined, undefined, this.unreadOnly, 1, 10).subscribe(r => {
      this.alertsReceived = r.items;
      this.fetchRecvInProgress = false;
    });
  }

  onUnreadToggle(selected: boolean) {
    this.unreadOnly = selected === true ? true : null;
    this.alertsReceived = null;
    this.loadAlerts(true);
  }

  onMarkAllRead() {
    this.dialog.open(ConfirmComponent, {
      width: '400px',
      data: { title: 'Mark All as Read', message: 'Are you sure you want to mark all your received alerts as read?' }
    }).afterClosed().subscribe( result => {
      if (result) {
        this.fetchRecvInProgress = true;
        this.alertsApi.updateUnreadAlertsReceived().subscribe({
          next: _ => { this.loadAlerts(true); },
          error: _ => { 
            this.fetchRecvInProgress = false;
            this.snack.open('Failed to mark all alerts as read');
          }
        });
      }
    })
  }

  openHideDialog(alert: AlertSettings) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '250px',
      disableClose: true,
      data: { title: 'HideAlertDisclaimer', ok: 'Hide' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        const request: UpdateAlertsReceivedRequest = { updates: [{ id: alert.id, hide: true }] }
        this.alertsApi.updateAlertsReceived(request).subscribe(_ => {
          this.loadAlerts();
        });
      }
    });
  }

  getIncidentName(message?: MessageSettings): string {
    return IncidentModelHelper.getIncidentNameFromMessage(message);
  }
}
